import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { mockMemorials } from '../data/mockData';
import { useMemorialOwner } from '../hooks/useMemorialOwner';
import EventRsvpWaitlist from '../features/events/EventRsvpWaitlist';

const {
  FiCalendar, FiMapPin, FiClock, FiUsers, FiVideo,
  FiHeart, FiShare2, FiEdit3, FiCheck, FiX,
  FiType, FiImage, FiPalette, FiBell
} = FiIcons;

// Valid, working Unsplash gallery images
const GALLERY_IMAGES = [
  'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?q=80&w=800&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?q=80&w=800&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1516589174184-c685ca33d2b0?q=80&w=800&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?q=80&w=800&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?q=80&w=800&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=800&auto=format&fit=crop',
];

const BANNER_IMAGES = [
  'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=800',
  'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?auto=format&fit=crop&w=800',
  'https://images.unsplash.com/photo-1516589174184-c685ca33d2b0?auto=format&fit=crop&w=800',
  'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?auto=format&fit=crop&w=800',
];

const EventLanding = () => {
  const { memorialId, eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [memorial, setMemorial] = useState(null);
  const { isOwner } = useMemorialOwner(memorial);
  const [isEditing, setIsEditing] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  const [customConfig, setCustomConfig] = useState({
    themeColor: '#0A192F',
    fontFamily: 'serif',
    heroImage: BANNER_IMAGES[0],
    showGuestlist: true,
    allowTributes: true
  });

  useEffect(() => {
    const mem = mockMemorials.find(m => m.id === memorialId);
    if (mem) {
      setMemorial(mem);
      const ev = (mem.events || []).find(e => e.id === eventId);
      if (ev) setEvent(ev);
    }
  }, [memorialId, eventId]);

  // Show a helpful fallback if the event/memorial isn't found
  if (!memorial) {
    return (
      <div className="min-h-screen bg-brand-light flex items-center justify-center pt-24">
        <div className="text-center px-6">
          <div className="w-20 h-20 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6 border border-brand-dark">
            <span className="text-3xl">🕯️</span>
          </div>
          <h2 className="text-3xl font-serif text-accent-navy mb-3">Memorial Not Found</h2>
          <p className="text-text-muted mb-8 font-light">This memorial page could not be located.</p>
          <button
            onClick={() => navigate('/explore')}
            className="btn-primary px-8 py-4"
          >
            Browse Memorials
          </button>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-brand-light flex items-center justify-center pt-24">
        <div className="text-center px-6">
          <div className="w-20 h-20 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6 border border-brand-dark">
            <SafeIcon icon={FiCalendar} className="w-10 h-10 text-accent-gold/40" />
          </div>
          <h2 className="text-3xl font-serif text-accent-navy mb-3">Event Not Found</h2>
          <p className="text-text-muted mb-8 font-light">This event could not be found for the memorial.</p>
          <button
            onClick={() => navigate(`/memorial/${memorialId}`)}
            className="btn-primary px-8 py-4"
          >
            View Memorial
          </button>
        </div>
      </div>
    );
  }

  const handleSaveCustomization = () => setIsEditing(false);

  return (
    <div className={`min-h-screen bg-brand-light font-${customConfig.fontFamily}`}>
      {/* Hero Section */}
      <div className="relative h-[60vh] md:h-[70vh] overflow-hidden">
        <img
          src={customConfig.heroImage}
          alt={event.title}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = BANNER_IMAGES[0]; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6 max-w-4xl"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full text-white text-xs font-black uppercase tracking-[0.3em]">
              <SafeIcon icon={FiCalendar} className="text-accent-gold" /> {event.type}
            </div>
            <h1 className="text-4xl md:text-7xl font-serif text-white tracking-tight leading-tight">
              {event.title}
            </h1>
            <p className="text-xl md:text-2xl text-white/80 font-light max-w-2xl mx-auto">
              In loving memory of {memorial.name}
            </p>
          </motion.div>
        </div>

        <button
          onClick={() => navigate(-1)}
          className="absolute top-10 left-6 md:left-10 p-3 bg-white/10 backdrop-blur-md text-white rounded-full hover:bg-white/20 transition-all border border-white/10"
        >
          <SafeIcon icon={FiX} className="w-6 h-6" />
        </button>

        {isOwner && (
          <button
            onClick={() => setIsEditing(true)}
            className="absolute top-10 right-6 md:right-10 flex items-center gap-2 px-5 py-3 bg-accent-gold text-accent-navy rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:scale-105 transition-transform"
          >
            <SafeIcon icon={FiEdit3} /> Customize
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 -mt-20 relative z-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Info Bar */}
            <div className="bg-white rounded-[32px] border border-brand-dark p-6 md:p-10 shadow-xl flex flex-wrap gap-8 justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-light rounded-2xl flex items-center justify-center text-accent-gold border border-brand-dark">
                  <SafeIcon icon={FiCalendar} className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Date</p>
                  <p className="font-bold text-accent-navy">
                    {new Date(event.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-light rounded-2xl flex items-center justify-center text-accent-gold border border-brand-dark">
                  <SafeIcon icon={FiClock} className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Time</p>
                  <p className="font-bold text-accent-navy">
                    {new Date(event.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-light rounded-2xl flex items-center justify-center text-accent-gold border border-brand-dark">
                  <SafeIcon icon={FiMapPin} className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Location</p>
                  <p className="font-bold text-accent-navy">{event.location}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-[40px] border border-brand-dark p-8 md:p-12 shadow-sm">
              <h2 className="text-3xl font-serif text-accent-navy mb-6">About the Service</h2>
              <div className="text-text-main font-light leading-relaxed space-y-4">
                <p>{event.description}</p>
                <p>We invite family and friends to join us in celebrating a life well-lived. Your presence, whether in person or virtually, means the world to us during this time.</p>
              </div>
            </div>

            {/* Event Tabs */}
            <div className="bg-white rounded-[40px] border border-brand-dark overflow-hidden shadow-sm">
              <div className="flex border-b border-brand-dark">
                {['details', 'gallery', 'tributes'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 py-6 text-[10px] font-black uppercase tracking-[0.2em] transition-all border-b-4 ${
                      activeTab === tab
                        ? 'border-accent-gold text-accent-navy bg-brand-light/20'
                        : 'border-transparent text-text-muted hover:text-accent-navy'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <div className="p-8 md:p-12">
                {activeTab === 'details' && (
                  <div className="space-y-8">
                    <h3 className="text-xl font-serif text-accent-navy mb-4">Service Schedule</h3>
                    <div className="space-y-4">
                      {[
                        { time: '10:00 AM', event: 'Welcome & Opening Music' },
                        { time: '10:30 AM', event: 'Eulogy & Shared Memories' },
                        { time: '11:15 AM', event: 'Visual Tribute' },
                        { time: '11:45 AM', event: 'Closing Remarks' }
                      ].map((item, i) => (
                        <div key={i} className="flex gap-6 p-4 rounded-2xl bg-brand-light/40 border border-brand-dark">
                          <span className="font-black text-accent-gold text-xs min-w-[70px]">{item.time}</span>
                          <span className="text-accent-navy font-medium">{item.event}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {activeTab === 'gallery' && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {GALLERY_IMAGES.map((img, i) => (
                      <div key={i} className="aspect-square rounded-2xl overflow-hidden border border-brand-dark">
                        <img
                          src={img}
                          alt={`Gallery ${i + 1}`}
                          className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                          onError={(e) => { e.target.src = GALLERY_IMAGES[0]; }}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {activeTab === 'tributes' && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-4 text-accent-gold">
                      <SafeIcon icon={FiHeart} className="w-8 h-8" />
                    </div>
                    <h4 className="text-lg font-serif text-accent-navy mb-2">Leave a Tribute for this Event</h4>
                    <p className="text-sm text-text-muted mb-6">Share a specific memory from this service.</p>
                    <button className="btn-primary px-8 py-4">Add Tribute</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* RSVP Card */}
            <div className="bg-accent-navy rounded-[40px] p-8 md:p-10 text-white shadow-2xl shadow-accent-navy/40 sticky top-32">
              <h3 className="text-2xl font-serif mb-6 text-center">Will you be attending?</h3>
              {rsvpStatus ? (
                <div className="text-center space-y-4 py-4">
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto text-accent-gold border border-white/20">
                    <SafeIcon icon={FiCheck} className="w-8 h-8" />
                  </div>
                  <p className="font-bold text-lg">
                    {rsvpStatus === 'yes' ? "We'll see you there!" : "Thanks for letting us know."}
                  </p>
                  <button onClick={() => setRsvpStatus(null)} className="text-[10px] font-black uppercase tracking-widest text-white/60 hover:text-white underline">
                    Change Status
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <button
                    onClick={() => setRsvpStatus('yes')}
                    className="w-full py-5 bg-accent-gold text-accent-navy rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:scale-[1.02] transition-all"
                  >
                    I'll be there
                  </button>
                  <button
                    onClick={() => setRsvpStatus('no')}
                    className="w-full py-5 bg-white/10 backdrop-blur-md text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] border border-white/20 hover:bg-white/20 transition-all"
                  >
                    Regretfully decline
                  </button>
                  <div className="pt-6 border-t border-white/10 mt-6 space-y-4">
                    <button className="w-full flex items-center justify-center gap-3 py-4 text-[10px] font-black uppercase tracking-widest text-white/70 hover:text-white transition-colors">
                      <SafeIcon icon={FiBell} className="text-accent-gold" /> Set Reminder
                    </button>
                    <button className="w-full flex items-center justify-center gap-3 py-4 text-[10px] font-black uppercase tracking-widest text-white/70 hover:text-white transition-colors">
                      <SafeIcon icon={FiShare2} className="text-accent-gold" /> Share Invitation
                    </button>
                  </div>
                </div>
              )}
            </div>
            <EventRsvpWaitlist event={event} memorial={memorial} />

            {/* Livestream Card */}
            {event.type === 'Livestream' && (
              <div className="bg-white rounded-[40px] border border-brand-dark p-8 md:p-10 shadow-sm text-center">
                <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-100">
                  <SafeIcon icon={FiVideo} className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-serif text-accent-navy mb-2">Join Virtually</h3>
                <p className="text-sm text-text-muted mb-8">This service will be broadcasted live for those who cannot attend in person.</p>
                <button
                  onClick={() => navigate('/live-events')}
                  className="w-full py-5 bg-red-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-red-600/20 hover:bg-red-700 transition-all"
                >
                  Go to Live Events
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Customization Drawer */}
      <AnimatePresence>
        {isEditing && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsEditing(false)}
              className="fixed inset-0 bg-accent-navy/40 backdrop-blur-sm z-[200]"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-[201] shadow-2xl flex flex-col"
            >
              <div className="p-8 border-b border-brand-dark flex justify-between items-center bg-brand-light">
                <div>
                  <h2 className="text-2xl font-serif text-accent-navy">Page Editor</h2>
                  <p className="text-xs text-text-muted uppercase font-black tracking-widest mt-1">Customize your event landing</p>
                </div>
                <button onClick={() => setIsEditing(false)} className="p-2 hover:bg-brand-dark rounded-full transition-colors">
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              <div className="flex-grow overflow-y-auto p-8 space-y-10">
                {/* Theme Color */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-accent-navy font-bold">
                    <SafeIcon icon={FiPalette} /> Theme Color
                  </div>
                  <div className="grid grid-cols-5 gap-3">
                    {['#0A192F', '#1A2E35', '#2D3436', '#4A3427', '#1E3A8A'].map(color => (
                      <button
                        key={color}
                        onClick={() => setCustomConfig(c => ({ ...c, themeColor: color }))}
                        className={`w-10 h-10 rounded-full border-2 transition-all ${customConfig.themeColor === color ? 'border-accent-gold scale-110 shadow-lg' : 'border-transparent'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                {/* Typography */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-accent-navy font-bold">
                    <SafeIcon icon={FiType} /> Typography
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {['serif', 'sans'].map(font => (
                      <button
                        key={font}
                        onClick={() => setCustomConfig(c => ({ ...c, fontFamily: font }))}
                        className={`p-4 rounded-2xl border-2 text-sm capitalize ${customConfig.fontFamily === font ? 'border-accent-navy bg-brand-light' : 'border-brand-dark text-text-muted'}`}
                      >
                        {font} Style
                      </button>
                    ))}
                  </div>
                </div>

                {/* Banner Image */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-accent-navy font-bold">
                    <SafeIcon icon={FiImage} /> Banner Image
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {BANNER_IMAGES.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setCustomConfig(c => ({ ...c, heroImage: img }))}
                        className={`aspect-video rounded-xl overflow-hidden border-2 transition-all ${customConfig.heroImage === img ? 'border-accent-gold shadow-lg' : 'border-transparent opacity-60 hover:opacity-100'}`}
                      >
                        <img src={img} className="w-full h-full object-cover" alt={`Banner ${i + 1}`} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-6 pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-accent-navy font-bold">
                      <SafeIcon icon={FiUsers} /> Show Guestlist
                    </div>
                    <button
                      onClick={() => setCustomConfig(c => ({ ...c, showGuestlist: !c.showGuestlist }))}
                      className={`w-12 h-6 rounded-full transition-colors relative ${customConfig.showGuestlist ? 'bg-accent-gold' : 'bg-brand-dark'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${customConfig.showGuestlist ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-accent-navy font-bold">
                      <SafeIcon icon={FiHeart} /> Allow Tributes
                    </div>
                    <button
                      onClick={() => setCustomConfig(c => ({ ...c, allowTributes: !c.allowTributes }))}
                      className={`w-12 h-6 rounded-full transition-colors relative ${customConfig.allowTributes ? 'bg-accent-gold' : 'bg-brand-dark'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${customConfig.allowTributes ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-8 bg-brand-light border-t border-brand-dark flex gap-4">
                <button
                  onClick={handleSaveCustomization}
                  className="flex-1 btn-primary py-4 flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiCheck} /> Save Changes
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-6 py-4 bg-white border border-brand-dark text-text-muted rounded-2xl hover:text-accent-navy font-black uppercase text-[10px] tracking-widest transition-all"
                >
                  Discard
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventLanding;